<template>
  <SocioDialog
    data-testid="incertitude-modal"
    :model-value="modelValue"
    persistent
    scrollable
    :title="incertitude ? t('edit') : t('add')"
    :fullscreen="mobile"
    @keydown.esc="closeDialog"
    @cancel="closeDialog"
  >
    <template #textSlot>
      <SocioTextarea
        v-model="form.description"
        input-title="Description"
        class="mb-4"
        data-testid="textarea-description"
      />
      <SocioSelect
        v-if="incertitude"
        v-model="form.category"
        input-title="Catégorie"
        :items="incertitudeCategories"
        data-testid="select-category"
      />
    </template>
    <template #confirmSlot>
      <SocioBtn prepend-icon="mdi-check" :text="t('save')" @click="saveIncertitude" />
    </template>
  </SocioDialog>
</template>
<script setup>
import { useIncertitudeStore } from "@/store/modules/incertitude";
import { useCartographyStore } from "@/store/modules/cartography";
import { useTestimonialStore } from "@/store/modules/testimonials";
import { incertitudeCategories } from "@/constants/incertitude";
import { reactive, onMounted } from "vue";
import { useMissionStore } from "@/store/modules/mission";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { useNotificationsStore } from "@/store/modules/notification";

const { t } = useI18n();
const { mobile } = useDisplay();
const notificationsStore = useNotificationsStore();
const incertitudeStore = useIncertitudeStore();
const cartographyStore = useCartographyStore();
const testimonialsStore = useTestimonialStore();
const missionStore = useMissionStore();

const emit = defineEmits(["update:modelValue"]);
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  incertitude: {
    type: Object,
    default: null,
  },
});

const form = reactive({
  uuid: "",
  description: "",
  category: "",
});

const closeDialog = () => {
  emit("update:modelValue", false);
};
// Le masque de testimonialsStore.partialUpdate indexe `data`, en camelCase :
// des noms snake_case (convention gRPC) y résolvaient `undefined` et le PATCH
// ne partait qu'avec l'uuid.
const fields = ["incertitudeDescription", "incertitudeCategory"];
const saveIncertitude = async () => {
  try {
    if (props.incertitude?.historicalItemType) {
      await testimonialsStore.partialUpdate({
        data: {
          incertitudeDescription: form.description,
          incertitudeCategory: form.category,
          uuid: props.incertitude.uuid,
          typology: props.incertitude.historicalItemType,
        },
        fields,
      });
    } else if (props.incertitude?.label) {
      await cartographyStore.partialUpdate({
        data: {
          incertitudeDescription: form.description,
          incertitudeCategory: form.category,
          uuid: props.incertitude.uuid,
          typology: props.incertitude.typology,
        },
      });
    } else {
      await incertitudeStore.createOrUpdateIncertitude({
        project: missionStore.currentMissionUuid,
        ...form,
      });
    }
    notificationsStore.showSuccessNotification(t("saveSuccess"));
  } catch (error) {
    notificationsStore.showErrorNotification(t("saveError"));
    console.error("Error saving incertitude:", error);
  }
  closeDialog();
};

onMounted(() => {
  if (props.incertitude) {
    form.uuid = props.incertitude.uuid;
    form.description = props.incertitude.label
      ? props.incertitude.incertitudeDescription
      : props.incertitude.description;
    form.category = props.incertitude.label
      ? props.incertitude.incertitudeCategory
      : props.incertitude.category;
  }
});
</script>
<i18n>
{
  "fr": {
    "add": "Déclarer incertitude",
    "edit": "Editer incertitude",
    "save": "Valider",
    "saveSuccess": "Les informations ont été enregistrées avec succès.",
    "saveError": "Une erreur est survenue lors de l'enregistrement des informations."
  },
  "en": {
    "add": "Add incertitude",
    "edit": "Edit incertitude",
    "save": "Save",
    "saveSuccess": "Information saved successfully.",
    "saveError": "An error occurred while saving the information."
  }
}
</i18n>
